import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../components/AppShell";
import { api } from "../lib/api";
import { fmtINR } from "../lib/format";
import { useToast } from "../hooks/use-toast";
import { Receipt, TrendingUp, TrendingDown, FileText, Layers, Sparkles } from "lucide-react";
import ExportButtons from "../components/ExportButtons";
import { stampedName } from "../lib/csv";
import { fmtUnits } from "../lib/format";

// Build CSV/PDF payload for the currently selected P&L / Bonus statement
function buildPlExport(kind, current) {
  if (!current) return null;
  const fy = current.fiscalYear;
  if (kind === "bonus") {
    const headers = ["Member", "Units", "Holding %", "Bonus Issued"];
    const rows = (current.members || []).map((m) => [
      m.name,
      Number(m.units || 0).toFixed(4),
      Number(m.holdingPct || 0).toFixed(2),
      Number(m.bonusIssued || 0).toFixed(4),
    ]);
    const pdfColumns = headers;
    const pdfRows = (current.members || []).map((m) => [
      m.name,
      fmtUnits(m.units),
      Number(m.holdingPct || 0).toFixed(2) + "%",
      fmtUnits(m.bonusIssued),
    ]);
    return {
      filePrefix: `bonus_${fy.replace(/\s/g, "")}`,
      title: `Bonus Statement — ${fy}`,
      summary: [
        ["Equity PAT", fmtINR(current.equityPAT || 0)],
        ["Derivative PAT", fmtINR(current.derivativePAT || 0)],
        ["Total PAT", fmtINR(current.totalPAT || 0)],
        ["Bonus Pool Units", Number(current.bonusPoolUnits || 0).toFixed(4)],
        ["Total Bonus Issued", Number(current.totalBonusIssued || 0).toFixed(4)],
        ["Unit rate (1st April)", "₹" + Number(current.unitRateOnApril || 0).toFixed(4)],
      ],
      headers, rows, pdfColumns, pdfRows,
    };
  }
  // Equity / Derivative P&L (derivative has internal/external split)
  const isDer = kind === "derivative";
  const buildRows = (items) => (items || []).map((r) => isDer
    ? [r.name, r.internal || 0, r.external || 0, (r.internal || 0) + (r.external || 0)]
    : [r.name, r.amount || 0]);
  const section = (title, items) => {
    const rows = buildRows(items);
    rows.unshift(isDer ? [`— ${title} —`, "", "", ""] : [`— ${title} —`, ""]);
    return rows;
  };
  const rows = [
    ...section("Income", current.income),
    ...section("Expenses", current.expenses),
    ...section("Taxes", current.taxes),
  ];
  const headers = isDer ? ["Line Item", "Internal", "External", "Total"] : ["Line Item", "Amount"];
  const pdfRows = rows.map((r) => r.map((c) =>
    typeof c === "number" ? fmtINR(c, { decimals: 0 }) : c
  ));
  return {
    filePrefix: `${kind}_pl_${fy.replace(/\s/g, "")}`,
    title: `${isDer ? "Derivative" : "Equity"} P&L — ${fy}`,
    summary: [
      ["Gross Income", fmtINR(current.grossIncome || 0)],
      ["Total Expenses", fmtINR(current.totalExpenses || 0)],
      ["Total Tax", fmtINR(current.totalTax || 0)],
      ["EBITA", fmtINR(current.ebita || 0)],
      ["PAT", fmtINR(current.pat || 0)],
    ],
    headers, rows, pdfColumns: headers, pdfRows,
  };
}

export default function ProfitLoss() {
  const { toast } = useToast();
  const [kind, setKind] = useState("equity");
  const [eqList, setEqList] = useState([]);
  const [dList, setDList] = useState([]);
  const [bList, setBList] = useState([]);
  const [selectedEqFY, setSelectedEqFY] = useState(null);
  const [selectedDFY, setSelectedDFY] = useState(null);
  const [selectedBFY, setSelectedBFY] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    Promise.all([api.listPnl(), api.listDerivativePnl(), api.listBonus()])
      .then(([eq, der, bon]) => {
        if (!mounted) return;
        setEqList(eq);
        setDList(der);
        setBList(bon);
        if (eq.length > 0) setSelectedEqFY(eq[0].fiscalYear);
        if (der.length > 0) setSelectedDFY(der[0].fiscalYear);
        if (bon.length > 0) setSelectedBFY(bon[0].fiscalYear);
        setLoading(false);
      })
      .catch((e) => {
        if (!mounted) return;
        setLoading(false);
        toast({ title: "Failed to load P&L", description: e.response?.data?.detail || "Try again" });
      });
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const list = kind === "equity" ? eqList : kind === "derivative" ? dList : bList;
  const selectedFY =
    kind === "equity" ? selectedEqFY : kind === "derivative" ? selectedDFY : selectedBFY;
  const setSelectedFY =
    kind === "equity" ? setSelectedEqFY : kind === "derivative" ? setSelectedDFY : setSelectedBFY;

  const current = useMemo(
    () => list.find((s) => s.fiscalYear === selectedFY),
    [list, selectedFY]
  );
  const pl = useMemo(() => buildPlExport(kind, current), [kind, current]);

  return (
    <AppShell>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="inline-flex items-center gap-2 text-[10px] text-emerald-300/85 uppercase tracking-[0.22em] font-mono">
            <span className="pulse-dot" style={{ width: 6, height: 6 }} />
            <Receipt size={11} /> P&amp;L Statement
          </div>
          <h1 className="text-2xl md:text-4xl font-display text-white mt-1 tracking-tight">
            Profit &amp; Loss
          </h1>
          <p className="text-sm text-slate-400 mt-1 font-mono">
            Fiscal year income, expenses, tax, and PAT at a glance.
          </p>
        </div>
        {pl && (
          <ExportButtons
            testIdPrefix="pnl-export"
            csv={{
              filename: stampedName(pl.filePrefix),
              headers: pl.headers,
              rows: pl.rows,
            }}
            pdf={{
              title: pl.title,
              filename: pl.filePrefix,
              orientation: "portrait",
              columns: pl.pdfColumns,
              rows: pl.pdfRows,
              summary: pl.summary,
            }}
          />
        )}
      </div>

      {/* Kind toggle */}
      <div className="mt-5 flex gap-2 flex-wrap" data-testid="pnl-kind-tabs">
        <KindTab id="equity" label="Equity" icon={Receipt} active={kind === "equity"} onClick={setKind} />
        <KindTab id="derivative" label="Derivative" icon={Layers} active={kind === "derivative"} onClick={setKind} />
        <KindTab id="bonus" label="Bonus" icon={Sparkles} active={kind === "bonus"} onClick={setKind} />
      </div>

      {/* FY tabs */}
      {list.length > 0 && (
        <div className="mt-4 flex gap-2 overflow-x-auto no-scrollbar" data-testid="pnl-fy-tabs">
          {list.map((s) => (
            <button
              key={s.fiscalYear}
              onClick={() => setSelectedFY(s.fiscalYear)}
              data-testid={`pnl-fy-tab-${s.fiscalYear}`}
              className={`px-3 py-2 rounded-md text-xs font-medium border whitespace-nowrap transition-colors ${
                s.fiscalYear === selectedFY
                  ? "bg-slate-100 text-slate-950 border-slate-100"
                  : "bg-slate-900 text-slate-400 border-slate-800 hover:text-white"
              }`}
            >
              {s.fiscalYear}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="mt-8 text-sm text-slate-500">Loading…</div>
      ) : list.length === 0 ? (
        <div
          className="mt-8 bg-slate-900 border border-dashed border-slate-800 rounded-xl p-10 text-center"
          data-testid="pnl-empty-state"
        >
          <FileText className="mx-auto text-slate-600 mb-3" size={32} />
          <div className="text-sm text-slate-300 font-medium">
            No {kind === "equity" ? "Equity" : "Derivative"} P&amp;L statement yet
          </div>
          <div className="text-xs text-slate-500 mt-1">
            An admin can create one under Admin &rarr; P&amp;L Statement.
          </div>
        </div>
      ) : current ? (
        kind === "equity" ? (
          <EquityView statement={current} />
        ) : kind === "derivative" ? (
          <DerivativeView statement={current} />
        ) : (
          <BonusView statement={current} />
        )
      ) : null}
    </AppShell>
  );
}

function KindTab({ id, label, icon: Icon, active, onClick }) {
  return (
    <button
      onClick={() => onClick(id)}
      data-testid={`pnl-kind-${id}`}
      className={`flex items-center gap-2 px-4 h-10 rounded-md text-xs font-medium border whitespace-nowrap transition-colors ${
        active
          ? "bg-emerald-500 text-slate-950 border-emerald-500"
          : "bg-slate-900 text-slate-400 border-slate-800 hover:text-white"
      }`}
    >
      <Icon size={14} />
      {label}
    </button>
  );
}

/* ---------------- Equity view ---------------- */
function EquityView({ statement: s }) {
  return (
    <div className="mt-5 grid lg:grid-cols-5 gap-5" data-testid="pnl-statement-card">
      <div className="lg:col-span-3 bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        <SimpleSection
          title="Income from Equity"
          rows={s.income || []}
          totalLabel="Total Income"
          totalValue={s.grossIncome}
          positive
          testidPrefix="pnl-income"
        />
        <SimpleSection
          title="Expenses"
          rows={s.expenses || []}
          totalLabel="Total Expenses"
          totalValue={s.totalExpenses}
          negative
          testidPrefix="pnl-expense"
        />
        <SimpleSection
          title="Taxes"
          rows={s.taxes || []}
          totalLabel="Total Tax"
          totalValue={s.totalTax}
          negative
          testidPrefix="pnl-tax"
        />
      </div>

      <div className="lg:col-span-2 space-y-3 h-fit sticky top-6">
        <FinalFigures
          gross={s.grossIncome}
          totalExp={s.totalExpenses}
          totalTax={s.totalTax}
          ebita={s.ebita}
          pat={s.pat}
        />
        <MetaBlock notes={s.notes} updated_at={s.updated_at} />
      </div>
    </div>
  );
}

/* --------------- Derivative view --------------- */
function DerivativeView({ statement: s }) {
  return (
    <div className="mt-5 grid lg:grid-cols-5 gap-5" data-testid="dpnl-statement-card">
      <div className="lg:col-span-3 bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        <DerivativeSection
          title="Income from Derivative"
          rows={s.income || []}
          totals={s.incomeTotals}
          totalLabel="Total Income"
          testidPrefix="dpnl-income"
        />
        <DerivativeSection
          title="Expenses"
          rows={s.expenses || []}
          totals={s.expensesTotals}
          totalLabel="Total Expenses"
          testidPrefix="dpnl-expense"
        />
        <DerivativeSection
          title="Taxes"
          rows={s.taxes || []}
          totals={s.taxesTotals}
          totalLabel="Total Tax"
          testidPrefix="dpnl-tax"
        />
      </div>

      <div className="lg:col-span-2 space-y-3 h-fit sticky top-6">
        <FinalFigures
          gross={s.grossIncome}
          totalExp={s.totalExpenses}
          totalTax={s.totalTax}
          ebita={s.ebita}
          pat={s.pat}
        />
        <MetaBlock notes={s.notes} updated_at={s.updated_at} />
      </div>
    </div>
  );
}

/* ---------------- Bonus view ---------------- */
function BonusView({ statement: s }) {
  const members = s.members || [];
  return (
    <div className="mt-5 grid lg:grid-cols-5 gap-5" data-testid="bonus-statement-card">
      <div className="lg:col-span-3 bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        <SectionHeader title={`Bonus for ${s.fiscalYear}`} />
        <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-4 bg-slate-950/40 border-b border-slate-800">
          <BonusStat label="Equity PAT" value={s.equityPAT} />
          <BonusStat label="Derivative PAT" value={s.derivativePAT} />
          <BonusStat label="Total PAT" value={s.totalPAT} bold />
          <BonusStat
            label="Closing Capital (31 Mar)"
            value={s.closingCapital}
          />
          <BonusStat
            label="Closing Units (31 Mar)"
            raw={(s.closingUnits || 0).toFixed(4)}
          />
          <BonusStat
            label="Unit rate on 1st April"
            raw={`₹${(s.unitRateOnApril || 0).toFixed(4)}`}
          />
          <BonusStat
            label="Total Bonus Units Generated"
            raw={(s.totalBonusUnitsGenerated || 0).toFixed(4)}
          />
          <BonusStat
            label={`Bonus %`}
            raw={`${(s.bonusPctFY || 0).toFixed(2)}%`}
            bold
          />
        </div>

        <SectionHeader title="Members — Bonus Allocation" />
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-950/30">
                <th className="text-left font-normal px-5 py-2">Member</th>
                <th className="text-right font-normal px-5 py-2">Unit Balance</th>
                <th className="text-right font-normal px-5 py-2">Holding %</th>
                <th className="text-right font-normal px-5 py-2">Bonus Issued</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {members.map((m) => (
                <tr key={m.id} data-testid={`bonus-view-row-${m.id}`}>
                  <td className="px-5 py-2 text-slate-300">{m.name || "—"}</td>
                  <td className="px-5 py-2 text-right font-mono text-slate-100">
                    {(m.units || 0).toFixed(4)}
                  </td>
                  <td className="px-5 py-2 text-right font-mono text-slate-400">
                    {(m.holdingPct || 0).toFixed(2)}%
                  </td>
                  <td className="px-5 py-2 text-right font-mono text-amber-300">
                    {(m.bonusIssued || 0).toFixed(4)}
                  </td>
                </tr>
              ))}
              <tr className="bg-slate-950/60" data-testid="bonus-view-total">
                <td className="px-5 py-2.5 font-semibold text-white">Total</td>
                <td className="px-5 py-2.5 text-right font-mono font-semibold text-emerald-300">
                  {(s.totalMembersUnits || 0).toFixed(4)}
                </td>
                <td className="px-5 py-2.5 text-right font-mono font-semibold text-emerald-300">
                  100.00%
                </td>
                <td className="px-5 py-2.5 text-right font-mono font-semibold text-amber-300">
                  {(s.totalBonusIssued || 0).toFixed(4)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="lg:col-span-2 space-y-3 h-fit sticky top-6">
        <div
          className="rounded-xl p-5 border border-emerald-500/40 bg-emerald-500/5"
          data-testid="bonus-pool-card"
        >
          <div className="text-[11px] uppercase tracking-wider text-emerald-300 opacity-90">
            Bonus Pool Distributed
          </div>
          <div className="text-3xl font-semibold font-mono mt-1.5 text-emerald-300">
            {(s.totalBonusIssued || 0).toFixed(4)}
          </div>
          <div className="text-xs text-emerald-300/70 mt-1">units</div>
        </div>
        <div
          className="rounded-xl p-5 border border-slate-800 bg-slate-900"
          data-testid="bonus-pat-card"
        >
          <div className="text-[11px] uppercase tracking-wider text-slate-500">
            Total PAT
          </div>
          <div className="text-xl font-semibold font-mono mt-1.5 text-white">
            {fmtINR(s.totalPAT || 0, { decimals: 2 })}
          </div>
        </div>
        <MetaBlock notes={s.notes} updated_at={s.updated_at} />
      </div>
    </div>
  );
}

function BonusStat({ label, value, raw, bold }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`font-mono text-sm mt-0.5 ${bold ? "font-semibold text-white" : "text-slate-200"}`}>
        {raw != null ? raw : fmtINR(value || 0, { decimals: 2 })}
      </div>
    </div>
  );
}

/* ---------------- Section components ---------------- */
function SimpleSection({ title, rows, totalLabel, totalValue, positive, negative, testidPrefix }) {
  const color = positive ? "text-emerald-300" : negative ? "text-rose-300" : "text-white";
  return (
    <>
      <SectionHeader title={title} />
      <div className="divide-y divide-slate-800">
        {rows.map((r) => (
          <div
            key={r.id}
            className="flex items-center justify-between px-5 py-2.5 text-sm"
            data-testid={`${testidPrefix}-row-${r.id}`}
          >
            <span className="text-slate-300">{r.name || "—"}</span>
            <span className="font-mono text-slate-100">
              {fmtINR(r.amount || 0, { decimals: 2 })}
            </span>
          </div>
        ))}
        <div
          className="flex items-center justify-between px-5 py-3 text-sm bg-slate-950/60"
          data-testid={`${testidPrefix}-total`}
        >
          <span className={`font-semibold ${color}`}>{totalLabel}</span>
          <span className={`font-mono font-semibold ${color}`}>
            {fmtINR(totalValue || 0, { decimals: 2 })}
          </span>
        </div>
      </div>
    </>
  );
}

function DerivativeSection({ title, rows, totals, totalLabel, testidPrefix }) {
  return (
    <>
      <SectionHeader title={title} />
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-950/30">
              <th className="text-left font-normal px-5 py-2">Account Head</th>
              <th className="text-right font-normal px-5 py-2">Internal</th>
              <th className="text-right font-normal px-5 py-2">External</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {rows.map((r) => (
              <tr key={r.id} data-testid={`${testidPrefix}-row-${r.id}`}>
                <td className="px-5 py-2 text-slate-300">{r.name || "—"}</td>
                <td className="px-5 py-2 text-right font-mono text-slate-100">
                  {fmtINR(r.internal || 0, { decimals: 2 })}
                </td>
                <td className="px-5 py-2 text-right font-mono text-slate-100">
                  {fmtINR(r.external || 0, { decimals: 2 })}
                </td>
              </tr>
            ))}
            <tr className="bg-slate-950/60" data-testid={`${testidPrefix}-total`}>
              <td className="px-5 py-2.5 font-semibold text-white">{totalLabel}</td>
              <td className="px-5 py-2.5 text-right font-mono font-semibold text-amber-300">
                {fmtINR((totals || {}).internal || 0, { decimals: 2 })}
              </td>
              <td className="px-5 py-2.5 text-right font-mono font-semibold text-amber-300">
                {fmtINR((totals || {}).external || 0, { decimals: 2 })}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
}

/* ---------------- Final figures ---------------- */
function FinalFigures({ gross, totalExp, totalTax, ebita, pat }) {
  return (
    <div className="holo-border scan-sheen bg-slate-900/60 backdrop-blur-sm rounded-xl p-5" data-testid="pnl-final-figures">
      <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-3">
        Final Figures
      </div>
      <dl className="text-sm space-y-2">
        <KV k="Gross Income" v={fmtINR(gross || 0, { decimals: 2 })} positive />
        <KV k="− Total Expenses" v={fmtINR(totalExp || 0, { decimals: 2 })} negative />
        <KV k="− Total Tax" v={fmtINR(totalTax || 0, { decimals: 2 })} negative />
        <div className="h-px bg-slate-800 my-2" />
        <KV
          k="EBITA"
          v={fmtINR(ebita || 0, { decimals: 2 })}
          bold
          positive={(ebita || 0) >= 0}
          negative={(ebita || 0) < 0}
          icon={(ebita || 0) >= 0 ? TrendingUp : TrendingDown}
        />
        <KV
          k="PAT"
          v={fmtINR(pat || 0, { decimals: 2 })}
          bold
          big
          positive={(pat || 0) >= 0}
          negative={(pat || 0) < 0}
          icon={(pat || 0) >= 0 ? TrendingUp : TrendingDown}
        />
      </dl>
    </div>
  );
}

function SectionHeader({ title }) {
  return (
    <div className="px-5 py-3 bg-slate-950/40 border-b border-slate-800 text-[11px] font-semibold uppercase tracking-widest text-slate-400">
      {title}
    </div>
  );
}

function KV({ k, v, positive, negative, bold, big, icon: Icon }) {
  const color = positive ? "text-emerald-400" : negative ? "text-rose-400" : "text-slate-200";
  return (
    <div className="flex items-center justify-between">
      <dt className="flex items-center gap-2 text-slate-400 text-xs">
        {Icon && <Icon size={12} className={color} />}
        {k}
      </dt>
      <dd
        className={`font-mono ${big ? "text-2xl" : "text-sm"} ${
          bold ? "font-semibold text-white" : color
        }`}
      >
        {v}
      </dd>
    </div>
  );
}

function MetaBlock({ notes, updated_at }) {
  return (
    <>
      {notes && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 text-xs text-slate-400 whitespace-pre-wrap">
          {notes}
        </div>
      )}
      {updated_at && (
        <div className="text-[11px] text-slate-500 px-1">
          Last updated {new Date(updated_at).toLocaleString("en-IN")}
        </div>
      )}
    </>
  );
}
